/**
 * Workplan Template Import Modal
 * Import template structure from previous successful workplan reports
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Button, Input, Card, Badge, LoadingSpinner } from '@tracekit/core';
import { Search, FileText, Import, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { listReports } from '@tracekit/core';
import type { Report } from '@tracekit/core';
import { useWorkspace } from '@tracekit/core';
import { getAPIClient } from '@tracekit/core';

interface WorkplanTemplateImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  pastProjectId: string;
  projectId: string;
  onTemplateSelected: (template: {
    sections: Array<{ name: string; description: string }>;
    default_questions: Array<{ section: string; question: string }>;
    template_source?: string;
  }) => void;
}

export function WorkplanTemplateImportModal({
  isOpen,
  onClose,
  pastProjectId,
  projectId,
  onTemplateSelected,
}: WorkplanTemplateImportModalProps) {
  const { projectId: workspaceProjectId } = useWorkspace();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [adaptQuestions, setAdaptQuestions] = useState(true);

  // Fetch reports for selection (filter for workplan reports)
  const { data: reports, isLoading: reportsLoading } = useQuery({
    queryKey: ['reports', projectId || workspaceProjectId],
    queryFn: () => listReports(projectId || workspaceProjectId || ''),
    enabled: isOpen && !!(projectId || workspaceProjectId),
  });

  // Filter reports to show only workplan reports
  const filteredReports = useMemo(() => {
    if (!reports) return [];
    
    return reports.filter((r: Report) => {
      // Filter for workplan reports
      const metadata = (r as any).metadata || (r as any).meta || {};
      const isWorkplan =
        r.report_type === 'workplan_generation' ||
        (r.report_type === 'custom' &&
         metadata.report_type_override === 'workplan_generation');
      
      if (!isWorkplan) return false;
      
      // Apply search filter
      if (searchQuery) {
        const reportName = r.name || r.client_name || '';
        return reportName.toLowerCase().includes(searchQuery.toLowerCase());
      }
      
      return true;
    });
  }, [reports, searchQuery]);

  // Fetch template from selected report
  const { data: templateData, isLoading: templateLoading, error: templateError, refetch: refetchTemplate } = useQuery({
    queryKey: ['workplan-report-template', pastProjectId, selectedReportId, projectId, adaptQuestions],
    queryFn: async () => {
      if (!selectedReportId || !projectId || !pastProjectId) {
        throw new Error('Report ID, Project ID, and Past Project ID are required');
      }
      const api = getAPIClient();
      try {
        console.log(`[WorkplanTemplateImportModal] Fetching template from report ${selectedReportId} for past project ${pastProjectId}`);
        const data = await api.post(
          `/api/past-projects/${pastProjectId}/workplan-template/from-report?project_id=${projectId}`,
          {
            project_id: projectId,
            source_report_id: selectedReportId,
            adapt_questions: adaptQuestions,
          }
        );
        console.log(`[WorkplanTemplateImportModal] Template loaded:`, data);
        
        // Validate response structure
        if (!data) {
          throw new Error('Empty response from server');
        }
        
        if (data.sections === undefined && data.default_questions === undefined) {
          console.error('[WorkplanTemplateImportModal] Invalid response structure:', data);
          throw new Error('Invalid response structure from server - missing sections and default_questions');
        }
        
        return data;
      } catch (error: any) {
        console.error('[WorkplanTemplateImportModal] Failed to fetch template:', error);
        throw error;
      }
    },
    enabled: !!selectedReportId && !!projectId && !!pastProjectId && isOpen,
  });

  const handleImport = () => {
    if (templateData) {
      onTemplateSelected({
        sections: templateData.sections || [],
        default_questions: templateData.default_questions || [],
        template_source: templateData.template_source || 'previous_workplan',
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Workplan Template"
      size="large"
    >
      <div className="space-y-6">
        {/* Search */}
        <div>
          <Input
            placeholder="Search workplan reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search className="h-4 w-4" />}
          />
        </div>

        {/* Reports List */}
        {reportsLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : filteredReports.length === 0 ? (
          <Card className="p-8 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchQuery ? 'No workplan reports found matching your search' : 'No workplan reports available'}
            </p>
          </Card>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredReports.map((report: Report) => {
              const isSelected = selectedReportId === report.id;
              const metadata = (report as any).metadata || (report as any).meta || {};
              
              return (
                <Card
                  key={report.id}
                  className={`p-4 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  onClick={() => setSelectedReportId(report.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{report.name || 'Untitled Workplan'}</h4>
                        {isSelected && (
                          <CheckCircle className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {report.client_name || 'No client name'}
                      </p>
                      {report.created_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Created: {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline">Workplan</Badge>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Template Preview */}
        {selectedReportId && (
          <div className="border-t pt-4">
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={adaptQuestions}
                  onChange={(e) => setAdaptQuestions(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Adapt questions to new project context</span>
              </label>
            </div>

            {templateLoading ? (
              <div className="flex items-center gap-2 py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-gray-600">Loading template...</span>
              </div>
            ) : templateError ? (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">
                    {(templateError as any)?.response?.data?.detail || (templateError as Error)?.message || 'Failed to load template'}
                  </span>
                </div>
              </div>
            ) : templateData ? (
              <Card className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h4 className="font-semibold">Template Ready</h4>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Sections:</span>{' '}
                    <span className="font-semibold">{templateData.sections?.length || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Questions:</span>{' '}
                    <span className="font-semibold">{templateData.default_questions?.length || 0}</span>
                  </div>
                </div>
                {templateData.template_source && (
                  <p className="text-xs text-gray-500 mt-2">
                    Source: {templateData.template_source}
                  </p>
                )}
              </Card>
            ) : null}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!templateData || templateLoading}
            icon={<Import className="h-4 w-4" />}
          >
            Import Template
          </Button>
        </div>
      </div>
    </Modal>
  );
}
